import { useState, useEffect } from "react";
import { motion } from "framer-motion";

export default function ItemDetailSheet({ item, menu, cart, onConfirm, onClose, onAddSuggestion }) {
  const [selected, setSelected] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    document.body.style.overflow = "hidden";
    return () => { 
      window.removeEventListener("resize", handleResize);
      document.body.style.overflow = ""; 
    };
  }, []);

  const toggle = (addon) => {
    setSelected(prev =>
      prev.find(a => a.id === addon.id)
        ? prev.filter(a => a.id !== addon.id)
        : [...prev, addon]
    );
  };

  const totalExtra = selected.reduce((s, a) => s + a.price, 0);
  const totalPrice = item.price + totalExtra;

  // Suggestions: same category, not this item, available
  const suggestions = menu
    .filter(m => m.category === item.category && m.id !== item.id && m.available)
    .slice(0, 4);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1002, display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "center" }}
      onClick={onClose}
    >
      <motion.div
        onClick={e => e.stopPropagation()}
        drag={isMobile ? "y" : false}
        dragConstraints={{ top: 0 }}
        dragElastic={{ top: 0, bottom: 0.4 }}
        onDragEnd={(e, info) => {
          if (info.offset.y > 100 || info.velocity.y > 500) {
            onClose();
          }
        }}
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        style={{
          background: "var(--card)", borderRadius: "20px 20px 0 0",
          maxHeight: "92vh", overflowY: "auto",
          width: "100%", maxWidth: 560,
          position: "relative"
        }}
      >
        {isMobile && <div style={{ position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)", width: 40, height: 4, background: "rgba(255,255,255,0.9)", borderRadius: 2, zIndex: 10, boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />}
        
        {/* Image */}
        <div style={{ position: "relative", width: "100%", height: 300, background: "var(--light)", flexShrink: 0 }}>
          {item.image_url
            ? <img loading="lazy" src={item.image_url} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--border)" strokeWidth="1.2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
              </div>
          }
          {/* Close button (Desktop only) */}
          {!isMobile && (
            <button onClick={onClose} aria-label="Close" style={{ position: "absolute", top: 12, right: 12, width: 32, height: 32, borderRadius: "50%", background: "rgba(0,0,0,0.45)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          )}
          {/* Veg badge */}
          <div className={`veg-badge ${item.type}`} style={{ top: 12, left: 12 }} />
        </div>

        <div style={{ padding: "18px 20px 24px" }}>
          {/* Name + price */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
            <div style={{ fontFamily: "'Sora', sans-serif", fontSize: 22, fontWeight: 700, color: "var(--dark)", flex: 1, marginRight: 12 }}>{item.name}</div>
            <div style={{ fontFamily: "'Sora', sans-serif", fontSize: 20, fontWeight: 700, color: "var(--primary)", flexShrink: 0 }}>₹{item.price}</div>
          </div>
          {/* Category & Rating Row */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, color: "var(--mid)", textTransform: "capitalize", background: "var(--light)", padding: "4px 8px", borderRadius: 6, fontWeight: 600 }}>{item.category}</span>
            {item.rating_count > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <div style={{ display: "flex", gap: 2 }}>
                  {[1, 2, 3, 4, 5].map(star => {
                    const isFilled = star <= Math.round(item.avg_rating);
                    return (
                      <span key={star} style={{ fontSize: 14, color: isFilled ? "#FFB000" : "#D1D5DB", lineHeight: 1 }}>★</span>
                    );
                  })}
                </div>
                <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 13, fontWeight: 700, color: "var(--dark)", marginLeft: 2 }}>{item.avg_rating}</span>
                <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 12, color: "var(--mid)" }}>({item.rating_count} rating{item.rating_count !== 1 ? "s" : ""})</span>
              </div>
            )}
          </div>

          {/* Addons */}
          {item.addons && item.addons.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--dark)", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.04em" }}>Customise</div>
              {item.addons.map(addon => {
                const active = !!selected.find(a => a.id === addon.id);
                return (
                  <div key={addon.id} onClick={() => toggle(addon)}
                    style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", borderRadius: 10, border: active ? "1.5px solid var(--primary)" : "1.5px solid var(--border)", background: active ? "var(--primary-l)" : "var(--card)", marginBottom: 8, cursor: "pointer", transition: "all 0.15s" }}>
                    <div style={{ width: 20, height: 20, borderRadius: "50%", border: active ? "6px solid var(--primary)" : "2px solid var(--border)", flexShrink: 0, transition: "all 0.15s" }} />
                    <div style={{ flex: 1, fontSize: 14, fontWeight: 600, color: "var(--dark)" }}>{addon.name}</div>
                    {addon.price > 0 && <div style={{ fontSize: 13, fontWeight: 700, color: "var(--primary)" }}>+₹{addon.price}</div>}
                  </div>
                );
              })}
            </div>
          )}

          {/* Add to cart */}
          <button className="btn-primary" onClick={() => onConfirm(item, selected)} style={{ marginBottom: suggestions.length > 0 ? 24 : 0 }}>
            Add to Cart · ₹{totalPrice}
          </button>

          {/* Suggestions */}
          {suggestions.length > 0 && (
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--dark)", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.04em" }}>You might also like</div>
              <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 4, scrollbarWidth: "none" }} onTouchStart={e => e.stopPropagation()} onTouchEnd={e => e.stopPropagation()}>
                {suggestions.map(s => {
                  const inCart = cart[s.id]?.qty > 0;
                  return (
                    <div key={s.id} style={{ flexShrink: 0, width: 130, border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden", background: "var(--card)" }}>
                      <div style={{ height: 80, background: "var(--light)", overflow: "hidden" }}>
                        {s.image_url
                          ? <img loading="lazy" src={s.image_url} alt={s.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--border)" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg></div>
                        }
                      </div>
                      <div style={{ padding: "8px 10px" }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--dark)", marginBottom: 4, lineHeight: 1.3 }}>{s.name}</div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--primary)" }}>₹{s.price}</div>
                          <button onClick={() => onAddSuggestion(s)} aria-label={`Add ${s.name}`}
                            style={{ width: 24, height: 24, borderRadius: 6, background: inCart ? "var(--success)" : "var(--primary)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
